import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CreateEventRegistration,
  CreateEventRegistrationFields,
  EventRegistrationForm,
} from '../generated/api/dsv-public/model';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { extractFormValuesFromRegistrant, generateRandomKey, reduceToPriceSummaryMap } from '../util/helper';
import { ToggleableSingleRegistrationForm } from './TogglableSingleRegistrationForm';
import { useTranslation } from 'react-i18next';
import { useFormCheckpoint } from '../context/FormCheckpoint';
import { useFormValidation } from '../context/FormValidation';
import { useIsMobile } from '../hooks/use-is-mobile';
import { PricingSummary, RegistrantInfo } from './form.types';

type MultiRegistrationFormProps = {
  registrant?: RegistrantInfo;
  registrationForm?: EventRegistrationForm;
  handleSubmitForm: (eventRegistrations: CreateEventRegistration[]) => void;
  isSameFamily: boolean;
  copyRegistrantFields: boolean;
  isFamilyMembers: boolean;
  handleGoBack: () => void;
};

type SingleEventRegistrationFormData = CreateEventRegistration & {
  key: string;
};

export const MultiRegistrationForm: React.FC<MultiRegistrationFormProps> = ({
  registrant,
  registrationForm,
  handleSubmitForm,
  isSameFamily,
  copyRegistrantFields,
  isFamilyMembers,
  handleGoBack,
}) => {
  const [totalPricingSummaries, setTotalPricingSummaries] = useState<PricingSummary[][]>([]);
  const { t } = useTranslation();
  const { scrollToError, isValidForm, fieldsValidation, removeFormValidation } = useFormValidation();
  const { savedRegistrationEntries } = useFormCheckpoint();
  const isMobile = useIsMobile();

  const initFormValues: () => SingleEventRegistrationFormData[] = useCallback(() => {
    return [
      {
        key: generateRandomKey(),
        fields: {},
      } as SingleEventRegistrationFormData,
    ];
  }, []);

  const [deleteFormIndex, setDeleteFormIndex] = useState<number | undefined>();
  const [formValues, setFormValues] = useState<SingleEventRegistrationFormData[]>(initFormValues());
  const [isSubmitPressed, setIsSubmitPressed] = useState<boolean>(false);

  useEffect(() => {
    if (savedRegistrationEntries !== undefined && Array.isArray(savedRegistrationEntries)) {
      setFormValues(
        savedRegistrationEntries.map(entry => {
          return entry as SingleEventRegistrationFormData;
        })
      );
    }
  }, [savedRegistrationEntries]);

  const requiredFieldsAreFilled: boolean = useMemo(() => {
    let requiredIsFilled = true;
    formValues.forEach(curFormValues => {
      registrationForm?.fields?.forEach(field => {
        if (field.required && field.key && !curFormValues.fields[field.key]) {
          requiredIsFilled = false;
        }
      });
    });
    return requiredIsFilled;
  }, [formValues, registrationForm]);

  const onSubmit = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      if (event) {
        setIsSubmitPressed(true);
        console.info('Form submitted with the following values:');
        console.info(JSON.stringify(formValues));
      }

      if (!requiredFieldsAreFilled) {
        console.warn('Form invalid. Not all fields are filled.');
      }

      if (!isValidForm) {
        console.warn('Form invalid. Field data is invalid.');
      }

      if (!requiredFieldsAreFilled || !isValidForm) {
        const invalidFields = fieldsValidation.filter(field => !field.isValid).map(field => field.fieldKey);
        console.warn('The following fields are invalid:', invalidFields);
        scrollToError();
        return false;
      }
      handleSubmitForm(formValues);
    },
    [handleSubmitForm, formValues, requiredFieldsAreFilled, fieldsValidation, scrollToError, isValidForm]
  );

  const onForcedSubmit = useCallback(() => {
    handleSubmitForm(formValues);
  }, [handleSubmitForm, formValues]);

  const handleDeleteForm = useCallback((formIndex: number) => {
    setDeleteFormIndex(formIndex);
  }, []);

  const confirmDeleteForm = useCallback(() => {
    setFormValues(prev => prev.filter((val, ind) => ind !== deleteFormIndex));
    setTotalPricingSummaries(prev => prev.filter((val, ind) => ind !== deleteFormIndex));
    removeFormValidation(Number(deleteFormIndex));
    setDeleteFormIndex(undefined);
  }, [deleteFormIndex, removeFormValidation]);

  const closeDeleteFormDialog = useCallback(() => {
    setDeleteFormIndex(undefined);
  }, []);

  const handleUpdateForm = useCallback((formIndex: number, registrationValues: CreateEventRegistration) => {
    setFormValues(prev =>
      prev.map((formValues, index) => {
        if (index === formIndex) {
          return { ...formValues, ...registrationValues };
        }
        return formValues;
      })
    );
  }, []);

  const extractCopyOnFamilyFields = useCallback(
    (fields: CreateEventRegistrationFields) => {
      const prefilledEntries = registrationForm?.fields
        ?.filter(field => field.copyOnSameFamily)
        .map(field => {
          return [String(field.key), String(fields[String(field.key)] || '')];
        });
      return (prefilledEntries && Object.fromEntries(prefilledEntries)) || ({} as CreateEventRegistrationFields);
    },
    [registrationForm]
  );

  const prefilledFields: CreateEventRegistrationFields = useMemo(() => {
    if (formValues.length > 1 && formValues[0].fields !== undefined && isSameFamily) {
      return {
        ...extractCopyOnFamilyFields(formValues[0].fields),
        lastName: registrant?.lastName as any,
        email: registrant?.email as any,
        mobile: registrant?.mobile as any,
      };
    } else if (formValues.length === 1 && copyRegistrantFields) {
      return extractFormValuesFromRegistrant(registrant, registrationForm);
    } else if (formValues.length === 1 && isFamilyMembers) {
      return {
        lastName: registrant?.lastName as any,
        email: registrant?.email as any,
        mobile: registrant?.mobile as any,
      } as CreateEventRegistrationFields;
    }
    return {};
  }, [
    registrant,
    registrationForm,
    formValues,
    extractCopyOnFamilyFields,
    isSameFamily,
    copyRegistrantFields,
    isFamilyMembers,
  ]);

  const prefilledOptionValues = useMemo(() => {
    return formValues.map(formValue => formValue.options);
  }, [formValues]);

  const handleScrollToNewParticipant = () => {
    const participantElements = document.getElementsByClassName('registration-form--field-container');
    const element = participantElements.item(participantElements.length - 1);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 100;
      window.scrollTo({ behavior: 'smooth', top: offsetPosition });

      // select first field
      const firstField = element.querySelector('input, select, textarea');
      if (firstField && firstField instanceof HTMLElement) {
        firstField.focus();
      }
    }
  };

  const handleAdditionalForm = useCallback(() => {
    setFormValues(prev => prev.concat(initFormValues()));
    // 100ms delay to wait for the new form to be rendered
    setTimeout(() => {
      handleScrollToNewParticipant();
    }, 100);
  }, [initFormValues]);

  const handleUpdatePricingSummary = useCallback((index: number, pricingSummary: PricingSummary[]) => {
    // 100ms delay to wait for the form updates to be rendered
    setTimeout(() => {
      setTotalPricingSummaries(prev => [...prev.slice(0, index), pricingSummary, ...prev.slice(index + 1)]);
    }, 100);
  }, []);

  const totalSelectedPricingOptionsSummary = useMemo(() => {
    return Array.from(reduceToPriceSummaryMap(totalPricingSummaries).values());
  }, [totalPricingSummaries]);

  const isError = (!requiredFieldsAreFilled || !isValidForm) && isSubmitPressed;

  return (
    <div className="registration-form--field-container">
      <h2>{t('forms.multi-form.title')}</h2>
      {registrationForm &&
        formValues.map((form, formIndex) => (
          <ToggleableSingleRegistrationForm
            key={form.key}
            prefilledFields={formIndex === 0 ? { ...prefilledFields, ...form.fields } : prefilledFields}
            prefilledOptionValues={prefilledOptionValues[formIndex]}
            registrationForm={registrationForm}
            handleDeleteForm={handleDeleteForm}
            handleUpdateForm={handleUpdateForm}
            handleUpdatePricingSummary={handleUpdatePricingSummary}
            isDeletable={formIndex !== 0 || formValues.length > 1}
            formIndex={formIndex}
          />
        ))}

      {registrationForm?.pricingOptionGroups && totalSelectedPricingOptionsSummary.length ? (
        <Typography fontWeight="bold">
          Total:{' '}
          {Array.from(totalSelectedPricingOptionsSummary.values())
            .map(summary => `${Number(summary.totalPrice).toFixed(2)} ${summary.currency}`)
            .join(', ')}
        </Typography>
      ) : (
        ''
      )}

      <Stack direction={isMobile ? 'column' : 'row'} justifyContent="center" alignItems="center" spacing={2}>
        <Button onClick={handleGoBack} variant="outlined">
          {t('forms.common.back')}
        </Button>
        <Button variant="outlined" onClick={handleAdditionalForm}>
          {t('forms.multi-form.additional-participant')}
        </Button>
        <Button
          variant="outlined"
          onClick={onSubmit}
          onDoubleClick={onForcedSubmit}
          color={isError ? 'error' : 'primary'}
        >
          {registrationForm?.billingAddressRequired ? t('forms.common.next') : t('forms.multi-form.submit')}
        </Button>
      </Stack>
      <Dialog
        open={deleteFormIndex !== undefined}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t('forms.multi-form.delete-dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('forms.multi-form.delete-dialog.description')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDeleteFormDialog}>
            {t('forms.multi-form.delete-dialog.abort')}
          </Button>
          <Button variant="contained" color="error" onClick={confirmDeleteForm}>
            {t('forms.multi-form.delete-dialog.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
